import { PageType, theme, useResponsive } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";

import { appAtom } from "~/lib/app/atoms";
import { Edition, useEdition } from "~/lib/edition";
import { useCurrentPageType } from "~/lib/router/hooks";

import { EditionInvitation } from ".";

export const useEditionInvitation = (onClose?: () => void) => {
  const { ipGeoLocation } = useAtomValue(appAtom);
  const { isInitialHasSelected: isInitialHasSelectedEdition, update: updateEdition } = useEdition({
    initializeWithValue: true,
  });

  const { asPath } = useRouter();
  const isDesktop = useResponsive(theme.breakpoints.up("tablet"), false);
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const pageType = useCurrentPageType();

  const editionFromRoute = useMemo(() => {
    switch (asPath) {
      case "/hk":
      case "/hk/opinion":
        return Edition.HongKong;
      case "/asia":
      case "/asia/opinion":
        return Edition.Asia;
      case "/us":
      case "/us/opinion":
        return Edition.UnitedStates;
      default:
        return Edition.International;
    }
  }, [asPath]);

  const suggestEdition = useMemo(() => {
    switch (ipGeoLocation?.result?.country) {
      case "HK":
        return Edition.HongKong;
      case "US":
      case "CA":
        return Edition.UnitedStates;
      case "MY":
      case "SG":
      case "IN":
      case "AU":
      case "PH":
      case "ID":
      case "JP":
      case "TW":
      case "NZ":
      case "VN":
      case "KR":
      case "MO":
      case "CN":
        return Edition.Asia;
      default:
        return Edition.International;
    }
  }, [ipGeoLocation?.result?.country]);

  const handleEditionSet = useCallback(
    (edition?: Edition) => {
      updateEdition(edition ?? suggestEdition);
    },
    [suggestEdition, updateEdition],
  );

  const [isClose, setIsClose] = useState(false);
  const handleClose = () => {
    onClose?.();
    setIsClose(true);
  };

  const ipGeoLocationIsLoading = ipGeoLocation?.status === "loading";
  const result = { component: undefined, isLoading: ipGeoLocationIsLoading };

  // Show the edition invitation has below conditions
  // 1. desktop or mobile
  // 2. Current page is home page
  // 3. Geolocation is ready
  // 4. Edition in route is not same as suggested
  // 5. Edition cookie has no values
  // 6. Close state has value
  if (!isDesktop && !isMobile) return result;
  else if (pageType !== PageType.Homepage) return result;
  else if (ipGeoLocationIsLoading) return result;
  else if (editionFromRoute === suggestEdition) return result;
  else if (isInitialHasSelectedEdition) return result;
  else if (isClose) return result;

  return {
    ...result,
    component: (
      <EditionInvitation
        editionFromRoute={editionFromRoute}
        suggestEdition={suggestEdition}
        onClose={handleClose}
        onEditionSet={handleEditionSet}
      />
    ),
  };
};
